/**
 * <scrolling-items> custom element.
 * Wraps any element list and produces a seamless infinite marquee.
 * Speed is in pixels per second so visual velocity stays consistent
 * regardless of content length.
 *
 * Usage:
 *   <scrolling-items speed="60" direction="normal">
 *     <div class="marquee">
 *       <div class="marquee__track">
 *         <span class="marquee__item">Item 1</span>
 *         <span class="marquee__item">Item 2</span>
 *       </div>
 *     </div>
 *   </scrolling-items>
 *
 * Attributes:
 *   speed     — pixels per second (default: 60)
 *   direction — "normal" (left) or "reverse" (right) (default: "normal")
 */
class ScrollingItems extends HTMLElement {
  connectedCallback() {
    const track = this.querySelector('.marquee__track');
    if (!track) return;

    // Clone children once so the track contains 2× content. The translate
    // animation runs from 0 to -halfWidth (= start of clones, which equals
    // start of originals), giving a seamless infinite loop.
    if (track.dataset.cloned !== 'true') {
      const original = Array.from(track.children);
      original.forEach((node) => track.appendChild(node.cloneNode(true)));
      track.dataset.cloned = 'true';
    }

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const speed = Number(this.getAttribute('speed')) || 60;
    const direction = this.getAttribute('direction') === 'reverse' ? 'reverse' : 'normal';
    track.style.setProperty('--marquee-direction', direction);

    let lastHalfWidth = 0;
    const measure = () => {
      const halfWidth = Math.round(track.scrollWidth / 2);
      if (halfWidth <= 0 || halfWidth === lastHalfWidth) return;
      lastHalfWidth = halfWidth;
      const duration = halfWidth / speed;
      // Set end-x in absolute pixels — eliminates sub-pixel jank that
      // translateX(-50%) suffers when scrollWidth and CSS-computed-half
      // disagree by a fraction of a pixel.
      track.style.setProperty('--marquee-end-x', `-${halfWidth}px`);
      track.style.setProperty('--marquee-duration', `${duration}s`);
    };

    // Wait for fonts to settle before measuring — webfonts that load after
    // initial paint can shift item widths and produce a jarring restart.
    const start = () => requestAnimationFrame(() => requestAnimationFrame(measure));
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(start);
    } else {
      start();
    }

    // Recalculate only on actual viewport resize, with debounce — avoids
    // the ResizeObserver firing during normal layout settling.
    let resizeTimer = null;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(measure, 180);
    }, { passive: true });
  }
}

if (!customElements.get('scrolling-items')) {
  customElements.define('scrolling-items', ScrollingItems);
}
